import moment from "moment";
import {
  formatSuiteSectionTitle,
  sumDefaultSeatsFromSuites,
} from "utils/suiteDisplayUtils";

/**
 * Split free-text into trimmed lines (bullets).
 */
export function splitLines(text) {
  if (!text || typeof text !== "string") return [];
  return text
    .split(/\r?\n/)
    .map((s) => s.replace(/^[-•*\u2022\s]+/u, "").trim())
    .filter(Boolean);
}

/**
 * Same "Client requirement" label as legacy Head Terms / pdfData.
 */
export function clientRequirementTitle(row) {
  if (!row) return "";
  if (row.spaceUpdate === "Square Feet") {
    return `${row.totalArea ?? ""} Square Feet`.trim();
  }
  if (row.spaceUpdate === "Workstation") {
    return `${row.proposedWorkstation || row.workstations || ""} Workstation`.trim();
  }
  if (row.spaceUpdate === "WholeSuite") {
    return formatSuiteSectionTitle(row);
  }
  if (row.spaceUpdate === "Virtual") {
    return "Virtual Seat";
  }
  return "Virtual Seat";
}

/**
 * Workstation count for suite column (WS): explicit WS fields or WholeSuite seat sum.
 */
export function suiteWorkstationsLine(row) {
  if (!row) return null;
  const direct = Number(row.proposedWorkstation ?? row.workstations);
  if (Number.isFinite(direct) && direct > 0) {
    return `${direct} Workstation${direct === 1 ? "" : "s"}`;
  }
  if (row.spaceUpdate === "WholeSuite") {
    const seats = sumDefaultSeatsFromSuites(row);
    if (seats > 0) {
      return `${seats} Workstation${seats === 1 ? "" : "s"}`;
    }
  }
  return null;
}

const DISCLAIMER = {
  line1:
    "Whilst every reasonable care has been taken in preparing this proposal, we will not be held responsible for any inaccuracy in its content. All plans, information and specifications are subject to change as may be required and this shall not form as part of an offer or contract.",
  line2:
    "Proposal is subject to availability, management's approval and GST.",
};

/**
 * Data for LICENSE FEE slide (GEC/KBT PDF + preview). Derived from proposal row fields used in legacy pdfData.
 */
export function buildLicenseFeeSlideData(row) {
  if (!row) return null;

  const suiteTitle = clientRequirementTitle(row);
  const suiteBullets = splitLines(row.requirementBreakup);
  const wsLine = suiteWorkstationsLine(row);
  if (wsLine) {
    suiteBullets.unshift(wsLine);
  }

  const monthly = Number(row.monthlyRental);
  const monthlyDisplay = Number.isFinite(monthly)
    ? `S$${monthly.toLocaleString()}`
    : "—";

  const ct = row.contractTerm;
  const termLabel =
    ct != null && ct !== "" ? `(${ct}-month term)` : "";

  const creditsLines = [];
  if (
    row.cleaningAndRestorationFees != null &&
    row.cleaningAndRestorationFees !== ""
  ) {
    creditsLines.push(
      `S$${Number(row.cleaningAndRestorationFees).toLocaleString()} cleaning & restoration fees`
    );
  }
  if (row.escalation != null && row.escalation !== "") {
    creditsLines.push(
      `${row.escalation}% escalation on monthly rental & security deposit (year on year)`
    );
  }

  const notesLines = splitLines(row.notesToClient);
  const creditsFromNotes = [];
  const depositFromNotes = [];
  let notesMode = "credits";
  for (const line of notesLines) {
    const u = line.toUpperCase();
    if (u === "DEPOSIT:" || u.startsWith("DEPOSIT /")) {
      notesMode = "deposit";
      continue;
    }
    if (u === "CREDITS:" || u.startsWith("CREDITS:")) {
      notesMode = "credits";
      continue;
    }
    if (notesMode === "deposit") depositFromNotes.push(line);
    else creditsFromNotes.push(line);
  }
  if (creditsFromNotes.length) {
    creditsLines.push(...creditsFromNotes);
  }
  if (!creditsLines.length) {
    creditsLines.push("As per standard SmartWorks inclusions");
  }

  const depositRemarksLines = [];
  const sd = Number(row.securityDeposit);
  const mr = Number(row.monthlyRental);
  if (Number.isFinite(sd) && sd > 0) {
    if (
      sd <= 24 &&
      sd === Math.floor(sd) &&
      Number.isFinite(mr) &&
      sd * mr > 0
    ) {
      depositRemarksLines.push(
        `${sd} month(s) deposit (S$ ${Number(sd * mr).toLocaleString()})`
      );
    } else {
      depositRemarksLines.push(`S$ ${sd.toLocaleString()} security deposit`);
    }
  }
  const parkBits = [];
  if (row.noOfFreeFourWheelerParking != null) {
    parkBits.push(`${row.noOfFreeFourWheelerParking} four-wheeler parking`);
  }
  if (row.noOfFreeTwoWheelerParking != null) {
    parkBits.push(`${row.noOfFreeTwoWheelerParking} two-wheeler parking`);
  }
  if (parkBits.length) {
    depositRemarksLines.push(`Parking: ${parkBits.join(", ")}`);
  }
  if (depositFromNotes.length) {
    depositRemarksLines.push(...depositFromNotes);
  }
  if (!depositRemarksLines.length) {
    depositRemarksLines.push("Refer to proposal notes & Head Terms");
  }

  const validityText = row.proposalValidity
    ? `Proposal valid till ${moment(row.proposalValidity).format(
        "MMMM Do[,] YYYY"
      )}`
    : "";

  return {
    suiteTitle,
    suiteBullets,
    monthlyDisplay,
    termLabel,
    creditsLines,
    depositRemarksLines,
    validityText,
    disclaimerLine1: DISCLAIMER.line1,
    disclaimerLine2: DISCLAIMER.line2,
  };
}
